import { CharacterBehavior } from "./CharacterBehavior";
import { AttachmentRule, Behavior } from "./types";

export class RandomWalkBehavior extends Behavior {
  private walkRadius: number = 50; // Maximum distance from origin
  private walkInterval: number = 3000; // Base time between new destinations in milliseconds
  private walkIntervalVariation: number = 0.5; // Variation factor (0.5 = ±50%)
  private originPosition: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 };
  private intervalId: number | null = null;
  private isActive: boolean = true;
  private characterBehavior: CharacterBehavior | null = null;
  private currentDestination: { x: number; y: number; z: number } | null = null;
  private destinationThreshold: number = 0.5; // How close to get to destination before picking new one
  private isInitialized: boolean = false; // Track if we've successfully initialized

  static getAttachmentRules(): AttachmentRule[] {
    return [{ type: "class", selector: "random-walk" }];
  }

  onAttach(): void {
    console.log("RandomWalkBehavior attached to:", this.element);

    // Store the initial position as the origin point
    this.originPosition = {
      x: parseFloat(this.element.getAttribute("x") || "0"),
      y: parseFloat(this.element.getAttribute("y") || "0"),
      z: parseFloat(this.element.getAttribute("z") || "0"),
    };

    // Read configuration attributes
    this.readConfigurationAttributes();

    // Don't start random walk yet - wait for CharacterBehavior to be available
    console.log("RandomWalkBehavior: Waiting for CharacterBehavior to initialize...");
  }

  onDetach(): void {
    console.log("RandomWalkBehavior detached from:", this.element);
    this.stopRandomWalk();
  }

  private tryInitialize(): boolean {
    if (this.isInitialized) return true;

    // Try to get reference to CharacterBehavior on the same element
    this.characterBehavior = this.element.getBehavior(CharacterBehavior);
    if (!this.characterBehavior) {
      // CharacterBehavior not available yet, try again next tick
      return false;
    }

    console.log("RandomWalkBehavior: CharacterBehavior found, starting random walk");
    this.isInitialized = true;
    this.startRandomWalk();
    return true;
  }

  private readConfigurationAttributes(): void {
    // Read walk radius
    const radiusAttr = this.element.getAttribute("walk-radius");
    if (radiusAttr) {
      const radius = parseFloat(radiusAttr);
      if (!isNaN(radius) && radius > 0) {
        this.walkRadius = radius;
      }
    }

    // Read walk interval
    const intervalAttr = this.element.getAttribute("walk-interval");
    if (intervalAttr) {
      const interval = parseFloat(intervalAttr);
      if (!isNaN(interval) && interval > 0) {
        this.walkInterval = interval * 1000; // Convert seconds to milliseconds
      }
    }

    // Read walk interval variation
    const variationAttr = this.element.getAttribute("walk-interval-variation");
    if (variationAttr) {
      const variation = parseFloat(variationAttr);
      if (!isNaN(variation) && variation >= 0 && variation <= 1) {
        this.walkIntervalVariation = variation;
      }
    }

    // Read destination threshold
    const thresholdAttr = this.element.getAttribute("destination-threshold");
    if (thresholdAttr) {
      const threshold = parseFloat(thresholdAttr);
      if (!isNaN(threshold) && threshold > 0) {
        this.destinationThreshold = threshold;
      }
    }

    // Read origin override
    const originXAttr = this.element.getAttribute("walk-origin-x");
    const originYAttr = this.element.getAttribute("walk-origin-y");
    const originZAttr = this.element.getAttribute("walk-origin-z");

    if (originXAttr !== null) {
      this.originPosition.x = parseFloat(originXAttr);
    }
    if (originYAttr !== null) {
      this.originPosition.y = parseFloat(originYAttr);
    }
    if (originZAttr !== null) {
      this.originPosition.z = parseFloat(originZAttr);
    }

    console.log(
      `RandomWalk config: radius=${this.walkRadius}, interval=${this.walkInterval}ms, variation=${this.walkIntervalVariation}, threshold=${this.destinationThreshold}, origin=(${this.originPosition.x}, ${this.originPosition.y}, ${this.originPosition.z})`,
    );
  }

  private generateRandomDestination(): { x: number; y: number; z: number } {
    // Generate random point within a circle on the XZ plane around the origin
    const angle = Math.random() * 2 * Math.PI; // Random angle 0-360 degrees
    const distance = Math.random() * this.walkRadius; // Random distance within radius

    // Convert polar coordinates to cartesian on XZ plane
    const x = this.originPosition.x + distance * Math.cos(angle);
    const y = this.originPosition.y; // Keep Y constant at origin Y
    const z = this.originPosition.z + distance * Math.sin(angle);

    return { x, y, z };
  }

  private getCurrentPosition(): { x: number; y: number; z: number } {
    return {
      x: parseFloat(this.element.getAttribute("x") || "0"),
      y: parseFloat(this.element.getAttribute("y") || "0"),
      z: parseFloat(this.element.getAttribute("z") || "0"),
    };
  }

  private getDistanceToDestination(): number {
    if (!this.currentDestination) return 0;

    const currentPos = this.getCurrentPosition();
    const dx = this.currentDestination.x - currentPos.x;
    const dy = this.currentDestination.y - currentPos.y;
    const dz = this.currentDestination.z - currentPos.z;

    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  private scheduleNextDestination(): void {
    if (!this.isActive) return;

    // Calculate next interval with variation
    const variation = (Math.random() - 0.5) * 2 * this.walkIntervalVariation;
    const actualInterval = this.walkInterval * (1 + variation);

    console.log(`RandomWalk: Scheduling next destination in ${actualInterval}ms`);

    this.intervalId = window.setTimeout(() => {
      console.log(`RandomWalk: Timer fired, setting new destination`);
      this.setRandomDestination();
      this.scheduleNextDestination(); // Schedule the next one
    }, actualInterval);
  }

  private setRandomDestination(): void {
    if (!this.isActive) return;

    this.currentDestination = this.generateRandomDestination();

    console.log(
      `RandomWalk: Setting destination to (${this.currentDestination.x.toFixed(2)}, ${this.currentDestination.y.toFixed(2)}, ${this.currentDestination.z.toFixed(2)})`,
    );
  }

  private moveTowardsDestination(deltaTime: number): void {
    if (!this.currentDestination || !this.characterBehavior) return;

    const currentPos = this.getCurrentPosition();

    // Calculate direction to destination
    const dx = this.currentDestination.x - currentPos.x;
    const dy = this.currentDestination.y - currentPos.y;
    const dz = this.currentDestination.z - currentPos.z;

    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

    // Check if we've reached the destination
    if (distance < this.destinationThreshold) {
      console.log("RandomWalk: Reached destination");
      this.currentDestination = null;
      // Stop movement
      this.characterBehavior.applyMovement({ x: 0, y: 0, z: 0 }, deltaTime);
      return;
    }

    // Normalize direction
    const direction = {
      x: dx / distance,
      y: dy / distance,
      z: dz / distance,
    };

    // Apply speed ramping as we approach the destination
    // Slow down when within 2x the threshold distance to prevent overshooting
    const slowdownDistance = this.destinationThreshold * 2;
    let speedMultiplier = 1.0;

    if (distance < slowdownDistance) {
      // Linear ramp down from full speed to 10% speed
      speedMultiplier = Math.max(0.1, distance / slowdownDistance);
    }

    // Apply movement through CharacterBehavior with speed control
    const scaledDirection = {
      x: direction.x * speedMultiplier,
      y: direction.y * speedMultiplier,
      z: direction.z * speedMultiplier,
    };

    this.characterBehavior.applyMovement(scaledDirection, deltaTime);
  }

  private startRandomWalk(): void {
    // Set initial random destination
    this.setRandomDestination();

    // Schedule the next destination with randomized timing
    this.scheduleNextDestination();

    console.log(
      `RandomWalk started with base interval ${this.walkInterval}ms and ${(this.walkIntervalVariation * 100).toFixed(0)}% variation`,
    );
  }

  private stopRandomWalk(): void {
    this.isActive = false;

    if (this.intervalId !== null) {
      clearTimeout(this.intervalId);
      this.intervalId = null;
    }

    // Clear current destination
    this.currentDestination = null;

    // Stop movement with proper deltaTime
    if (this.characterBehavior) {
      this.characterBehavior.applyMovement({ x: 0, y: 0, z: 0 }, 1 / 60); // Use standard 60fps deltaTime
    }

    console.log("RandomWalk stopped");
  }

  tick(deltaTime: number): void {
    // Try to initialize if we haven't yet
    if (!this.isInitialized) {
      if (!this.tryInitialize()) {
        // CharacterBehavior not available yet, try again next tick
        return;
      }
    }

    // Check if configuration attributes have changed
    const currentRadius = this.element.getAttribute("walk-radius");
    const currentInterval = this.element.getAttribute("walk-interval");
    const currentVariation = this.element.getAttribute("walk-interval-variation");

    let configChanged = false;

    if (currentRadius) {
      const radius = parseFloat(currentRadius);
      if (!isNaN(radius) && radius > 0 && radius !== this.walkRadius) {
        this.walkRadius = radius;
        configChanged = true;
      }
    }

    if (currentInterval) {
      const interval = parseFloat(currentInterval) * 1000;
      if (!isNaN(interval) && interval > 0 && interval !== this.walkInterval) {
        this.walkInterval = interval;
        configChanged = true;

        // Restart the scheduling with new timing
        if (this.intervalId !== null) {
          clearTimeout(this.intervalId);
          this.scheduleNextDestination();
        }
      }
    }

    if (currentVariation) {
      const variation = parseFloat(currentVariation);
      if (
        !isNaN(variation) &&
        variation >= 0 &&
        variation <= 1 &&
        variation !== this.walkIntervalVariation
      ) {
        this.walkIntervalVariation = variation;
        configChanged = true;
      }
    }

    if (configChanged) {
      console.log(
        `RandomWalk config updated: radius=${this.walkRadius}, interval=${this.walkInterval}ms, variation=${this.walkIntervalVariation}`,
      );
    }

    // Check if the element has been paused
    const isPaused = this.element.hasAttribute("walk-paused");
    if (isPaused && this.isActive) {
      this.isActive = false;
      if (this.intervalId !== null) {
        clearTimeout(this.intervalId);
        this.intervalId = null;
      }
      // Clear current destination and stop movement
      this.currentDestination = null;
      if (this.characterBehavior) {
        this.characterBehavior.applyMovement({ x: 0, y: 0, z: 0 }, deltaTime);
      }
      console.log("RandomWalk paused");
    } else if (!isPaused && !this.isActive && this.intervalId === null && this.isInitialized) {
      this.isActive = true;
      this.startRandomWalk();
      console.log("RandomWalk resumed");
    }

    // Apply movement towards current destination if we have one
    if (this.isActive && this.currentDestination && this.isInitialized) {
      this.moveTowardsDestination(deltaTime);
    }
  }
}
